import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import {
  IHistoryIndex,
  IIndex,
  useHistoryIndicesByIdAndPeriodLazyQuery
} from '../../generated/graphql';
import DialogBox from '../Common/Layout/DialogBox';
import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EpraDatePicker from '../Common/Fields/EpraDatePicker';
import CancelButton from '../Common/Buttons/CancelButton';
import SaveButton from '../Common/Buttons/SaveButton';
import { exportDataAsExcel } from '../../utils/fileUtiles';

type IProps = {
  handleClose: () => void;
  selectedIndex: IIndex | null;
};

type IErrors = {
  exportStartDate: string | null;
  exportEndDate: string | null;
  exportDateInvalidDate: string | null;
};

const defaultErrorsObj: IErrors = {
  exportStartDate: null,
  exportEndDate: null,
  exportDateInvalidDate: null
};

const errorTexts: IErrors = {
  exportStartDate: 'Export Start Date is required',
  exportEndDate: 'Export End Date is required',
  exportDateInvalidDate: 'Invalid Date Format'
};

const maxDate = new Date();

const ExportHIDataForm: React.FunctionComponent<IProps> = ({
  handleClose,
  selectedIndex
}: IProps) => {
  const [exportStartDate, setExportStartDate] = useState<Date | null>(new Date());
  const [exportEndDate, setExportEndDate] = useState<Date | null>(new Date());
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const { dispatch } = useContext(AppContext);
  const [getHistoryIndicesByIdAndPeriodData, { data }] = useHistoryIndicesByIdAndPeriodLazyQuery();
  const [historyIndicesData, setHistoryIndicesData] = useState<IHistoryIndex[]>([]);

  useEffect(() => {
    if (data && data.result) {
      setHistoryIndicesData(data.result as IHistoryIndex[]);
      handleExport(data.result);
    }
  }, [data]);

  const handleCancel = () => {
    setExportStartDate(null);
    setExportEndDate(null);
    handleClose();
  };

  const convertTimestampToDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
  };

  const handleExport = (exportData: IHistoryIndex[]) => {
    const fileName = 'HistoryIndicesData';
    const csvData = exportData.map((hi) => ({
      Id: hi.id,
      index_id: hi.index_id,
      date: convertTimestampToDate(Number(hi.date)),
      file_name: hi.file_name,
      dividend_yield: hi.dividend_yield,
      mkt_cap_aud: hi.mkt_cap_aud,
      mkt_cap_eur: hi.mkt_cap_eur,
      mkt_cap_jpy: hi.mkt_cap_jpy,
      mkt_cap_local: hi.mkt_cap_local,
      mkt_cap_usd: hi.mkt_cap_usd,
      price_close_aud: hi.price_close_aud,
      price_close_eur: hi.price_close_eur,
      price_close_gbp: hi.price_close_gbp,
      price_close_jpy: hi.price_close_jpy,
      price_close_local: hi.price_close_local,
      price_close_usd: hi.price_close_usd,
      price_net_aud: hi.price_net_aud,
      price_net_eur: hi.price_net_eur,
      price_net_gbp: hi.price_net_gbp,
      price_net_jpy: hi.price_net_jpy,
      price_net_local: hi.price_net_local,
      price_net_usd: hi.price_net_usd,
      price_return_aud: hi.price_return_aud,
      price_return_eur: hi.price_return_eur,
      price_return_gbp: hi.price_return_gbp,
      price_return_jpy: hi.price_return_jpy,
      price_return_local: hi.price_return_local,
      price_return_usd: hi.price_return_usd,
      dividend_net_yield: hi.dividend_net_yield,
      mkt_cap_hkd: hi.mkt_cap_hkd,
      price_close_hkd: hi.price_close_hkd,
      price_net_hkd: hi.price_net_hkd,
      price_return_hkd: hi.price_return_hkd,
      price_return_epra_local: hi.price_return_epra_local,
      price_close_epra_local: hi.price_close_epra_local
    }));
    exportDataAsExcel(csvData, fileName);
    handleClose();
  };

  const getPureDate = (date: Date | null) => {
    const getMonthWithTwoDigit = (date: Date) => {
      return ('0' + (date?.getMonth() + 1)).slice(-2);
    };

    const getDateWithTwoDigit = (date: Date | null) => {
      return ('0' + date?.getDate()).slice(-2);
    };
    const pureDate = `${date?.getFullYear()}-${getMonthWithTwoDigit(
      date as Date
    )}-${getDateWithTwoDigit(date)}`;
    return pureDate;
  };

  const exportMaxDateMessage = `Date should be before ${getPureDate(maxDate)}`;
  const exportEndDateErrorMessage = `End Date should be after Start Date`;

  const handleExportHIData = () => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `'Are you sure you want to export History Indices Data for period ${getPureDate(
          exportStartDate
        )} and ${getPureDate(exportEndDate)} ?'`,
        agreeAction: exportHSdata
      }
    });
  };

  const exportHSdata = async () => {
    const index_id = selectedIndex?.id;
    await getHistoryIndicesByIdAndPeriodData({
      variables: {
        input: {
          index_id: Number(index_id),
          start_date: exportStartDate?.toString() as string,
          end_date: exportEndDate?.toString() as string
        }
      }
    }).catch((error) => {
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: error.message,
          autoHide: 6000
        }
      });
    });
    dispatch({ type: Actions.HideConfirmation });
  };

  const handleExportStartDateChange = (value: Date | null) => {
    if (value) {
      if (getPureDate(value) > getPureDate(maxDate)) {
        errorTexts.exportStartDate = exportMaxDateMessage;
        setFormErrors({ ...formErrors, exportStartDate: errorTexts.exportStartDate });
      } else {
        setFormErrors({ ...formErrors, exportStartDate: null });
      }
    } else {
      setFormErrors({ ...formErrors, exportStartDate: errorTexts.exportStartDate });
    }
    setExportStartDate(value);
  };

  const handleExportEndDateChange = (value: Date | null) => {
    if (value) {
      if (getPureDate(value) > getPureDate(maxDate)) {
        errorTexts.exportEndDate = exportMaxDateMessage;
        setFormErrors({ ...formErrors, exportEndDate: errorTexts.exportEndDate });
      } else if (getPureDate(value) < getPureDate(exportStartDate)) {
        errorTexts.exportEndDate = exportEndDateErrorMessage;
        setFormErrors({ ...formErrors, exportEndDate: errorTexts.exportEndDate });
      } else {
        setFormErrors({ ...formErrors, exportEndDate: null });
      }
    } else {
      setFormErrors({ ...formErrors, exportEndDate: errorTexts.exportEndDate });
    }
    setExportEndDate(value);
  };

  const handleSubmit = () => {
    const errors = {
      exportStartDate: null,
      exportEndDate: null,
      exportDateInvalidDate: null
    } as IErrors;
    let hasErrors = false;
    if (!exportStartDate) {
      hasErrors = true;
      errors.exportStartDate = errorTexts.exportStartDate;
    } else {
      if (!Date.parse(exportStartDate.toString())) {
        hasErrors = true;
        errors.exportStartDate = errorTexts.exportDateInvalidDate;
      }
    }
    if (!exportEndDate) {
      hasErrors = true;
      errors.exportEndDate = errorTexts.exportEndDate;
    } else {
      if (!Date.parse(exportEndDate.toString())) {
        hasErrors = true;
        errors.exportEndDate = errorTexts.exportDateInvalidDate;
      }
    }
    if (hasErrors) {
      setFormErrors(errors);
    } else {
      handleExportHIData();
    }
  };

  return (
    <DialogBox
      id="history-indices-export-form"
      width={375}
      title="Export History Indices Data"
      onClose={handleClose}
    >
      <Box sx={{ margin: '50px', display: 'flex', flexDirection: 'column', gap: '45px' }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <EpraDatePicker
            id="export-start-date-picker"
            label="Start Date"
            value={exportStartDate}
            onChange={(date) => handleExportStartDateChange(date)}
            errorMsg={formErrors.exportStartDate}
            sx={{ width: '100%' }}
          />
          <EpraDatePicker
            id="export-end-date-picker"
            label="End Date"
            value={exportEndDate}
            onChange={(date) => handleExportEndDateChange(date)}
            errorMsg={formErrors.exportEndDate}
            sx={{ width: '100%' }}
          />
        </LocalizationProvider>
        <Box className="centralize">
          <CancelButton id="cancel-export-button" onClick={handleCancel} text="Close" />
          <SaveButton id="save-export-button" onClick={handleSubmit} text="Export" />
        </Box>
      </Box>
    </DialogBox>
  );
};

export default ExportHIDataForm;
